/**
 * 通讯器 · 星球活动（世界运营第二期 2a 的玩家端，I 看板 #8；CR-WORLD-OPS-PHASE2A-2026-09-27 第 0、3 节）。
 * 数据（契约 generated bf690313）：
 * - GET /events → PlanetEventList：对玩家开放、已批准或进行中、时间窗没结束的公共活动，只有公开字段（免登录）；
 * - /event-tells（带当前宠物的 pet_id）：GET 这只宠物告诉过的活动；POST {event_id} 告诉 TA；DELETE /event-tells/{event_id} 不告诉了。
 *   状态由服务端推导：active（告诉了）/ withdrawn（不告诉了）/ used（TA 自己在时间窗里去了，带 journey_id）/ void（活动结束或取消）。
 * 规矩（CR 第 0 节）：
 * - “告诉 TA”只是建议，去不去由 TA 自己决定；没有“报名”“替 TA 出发”；
 * - 不推送、不群发：活动只在玩家自己打开的这一页里；文案不催促、不制造愧疚；
 * - 不告诉了、或者一直没理会，之后都不再提这个活动：没有提示、红点、角标、“新”，列表里照常排着、样子和没告诉过的一样；
 * - used 照实说“TA 自己去了”，链到那一趟；void 不列。
 * D10（2026-09-27，用户定“家人看得见、可撤回”）：TA 自己也会听说活动，告诉列表里多出 source=heard 的行（缺 source 按 owner）。
 * 同一个活动可能有两行（家人告诉的、TA 自己听说的），页面合成一条：任一行 used 就是“TA 自己去了”；
 * 否则只有家人告诉的是“已经告诉 TA 了”，只有听说的是“TA 自己听说的”，两样都有是“家人告诉过（TA 也听说了）”；
 * “不告诉了”只撤家人那一行，“撤回”听说的那一行要带 source=heard；“TA 去过的活动”按活动去重。
 * 本文件只放纯函数：排列与状态、时间窗和地点的说法、拒绝的说法、查询键。服务实现在 ./events.service（演示与 live 两套）。
 */
import type { PlanetEvent, PlanetEventTell, PlanetEventTellSource } from "@/shared/contracts";
import { toApiError } from "@/shared/api/errors";

/** 这只宠物和一个活动的关系（页面按它决定按钮）：没告诉过（或不告诉了、作废）/ 家人告诉了 / TA 自己听说的 / 两样都有 / TA 自己去了。 */
export type TellState = "none" | "told" | "heard" | "told_and_heard" | "went";

export interface EventRow {
  event: PlanetEvent;
  state: TellState;
  /** state 为 went 时那一趟的行程号（链到那一趟）；其余为 null。 */
  journeyId: string | null;
}

/** 这一行是谁的：家人告诉的（owner）还是 TA 自己听说的（heard）；旧后端没有这个字段按 owner。 */
export function sourceOf(tell: Pick<PlanetEventTell, "source">): PlanetEventTellSource {
  return tell.source === "heard" ? "heard" : "owner";
}

/**
 * 这只宠物和一个活动的几行（家人告诉的、TA 自己听说的）合起来对应到页面上的关系：
 * 任一行 used → TA 自己去了（带那一趟）；否则看 active 的行；withdrawn、void 都当没有（不再提，也不标“撤回过”）。
 * 也收单独一行（旧写法）。
 */
export function tellStateOf(tells: PlanetEventTell | readonly PlanetEventTell[] | undefined): { state: TellState; journeyId: string | null } {
  const list = tells === undefined ? [] : Array.isArray(tells) ? tells : [tells as PlanetEventTell];
  const used = list.filter((tell) => tell.status === "used");
  if (used.length) return { state: "went", journeyId: used.find((tell) => tell.journey_id)?.journey_id ?? null };
  const told = list.some((tell) => tell.status === "active" && sourceOf(tell) === "owner");
  const heard = list.some((tell) => tell.status === "active" && sourceOf(tell) === "heard");
  return { state: told && heard ? "told_and_heard" : told ? "told" : heard ? "heard" : "none", journeyId: null };
}

/** 状态那一句（按钮上面）：家人告诉的、TA 自己听说的、两样都有；没告诉过、TA 自己去了不写这一句。 */
export function tellNote(state: TellState): string | null {
  if (state === "told") return "已经告诉 TA 了";
  if (state === "heard") return "TA 自己听说的";
  if (state === "told_and_heard") return "家人告诉过（TA 也听说了）";
  return null;
}

/** 撤的是哪一行：家人告诉过的（told、told_and_heard）撤家人那一行；只有听说的撤听说那一行；别的状态没有可撤的。 */
export function withdrawSourceFor(state: TellState): PlanetEventTellSource | null {
  if (state === "told" || state === "told_and_heard") return "owner";
  if (state === "heard") return "heard";
  return null;
}

const startsAt = (iso: string): number => {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
};

/**
 * 栏里的活动：开放中的活动按开始时间排（同时开始按标题），每个配上这只宠物的关系。
 * 顺序只看活动本身，不因为告诉过、不告诉了挪位置（不告诉了之后不再提：不置顶、不沉底、不加标记）。
 */
export function eventRows(events: readonly PlanetEvent[], tells: readonly PlanetEventTell[]): EventRow[] {
  // 一个活动可能有两行（家人告诉的、TA 自己听说的）：按活动收齐再合成
  const byEvent = new Map<string, PlanetEventTell[]>();
  for (const tell of tells) byEvent.set(tell.event_id, [...(byEvent.get(tell.event_id) ?? []), tell]);
  return [...events]
    .sort((a, b) => startsAt(a.window_start) - startsAt(b.window_start) || a.title.localeCompare(b.title, "zh-CN"))
    .map((event) => ({ event, ...tellStateOf(byEvent.get(event.event_id)) }));
}

/**
 * TA 去过、但已经不在开放列表里的活动（结束了）：照实记一笔“TA 自己去了”，链到那一趟。按活动结束时间从近到远。
 * 只收 used；活动结束了、没去的（void）和不告诉了的（withdrawn）都不列。
 * 同一个活动家人告诉的、TA 听说的两行都 used 时只列一条（优先带行程号的那行）。
 */
export function pastTrips(events: readonly PlanetEvent[], tells: readonly PlanetEventTell[]): PlanetEventTell[] {
  const open = new Set(events.map((event) => event.event_id));
  const byEvent = new Map<string, PlanetEventTell>();
  for (const tell of tells) {
    if (tell.status !== "used" || open.has(tell.event_id)) continue;
    const kept = byEvent.get(tell.event_id);
    if (!kept || (!kept.journey_id && tell.journey_id)) byEvent.set(tell.event_id, tell);
  }
  return [...byEvent.values()].sort((a, b) => Date.parse(b.window_end) - Date.parse(a.window_end));
}

/** 那一趟的地址：和行程页、地图的“TA 的旅行手账”同一个入口（/guides?journey=）。 */
export function tripHref(journeyId: string): string {
  return `/guides?journey=${encodeURIComponent(journeyId)}`;
}

/** 地点：去处的说法；有意向中的真实地点名字时跟在后面。 */
export function placeText(event: Pick<PlanetEvent, "destination_title" | "venue_name">): string {
  const venue = event.venue_name?.trim();
  return venue ? `${event.destination_title} · ${venue}` : event.destination_title;
}

const DAY = new Intl.DateTimeFormat("zh-CN", { month: "long", day: "numeric" });
const DAY_WITH_YEAR = new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "long", day: "numeric" });
const CLOCK = new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false });
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * 时间窗（本机时区，同公告页）：“9月28日 18:00 至 9月30日 22:00”；同一天写“9月28日 18:00 至 22:00”；不是今年的带上年份。
 * 只说起止，不写“还剩几天”“即将结束”这类催促的话。读不出来就是空串（页面不写这一行）。
 */
export function windowText(start: string, end: string, now: Date = new Date()): string {
  const a = new Date(start);
  const b = new Date(end);
  if (!Number.isFinite(a.getTime()) || !Number.isFinite(b.getTime())) return "";
  const day = (d: Date) => (d.getFullYear() === now.getFullYear() ? DAY : DAY_WITH_YEAR).format(d);
  const from = `${day(a)} ${CLOCK.format(a)}`;
  return sameDay(a, b) ? `${from} 至 ${CLOCK.format(b)}` : `${from} 至 ${day(b)} ${CLOCK.format(b)}`;
}

/** 告诉 TA、不告诉了被拒时的说法（按原因码，码本身不给玩家看）。 */
const REFUSALS: Record<string, string> = {
  event_not_open: "这个活动现在没有开放了。",
  destination_unavailable: "TA 现在去不了那里。",
  tell_already_used: "TA 已经去过这个活动了。",
  tell_not_found: "还没有告诉过 TA 这个活动。",
  // D10：TA 自己听说的那一行，开关关着时不许撤（后端 409）
  tell_not_withdrawable: "这个活动是 TA 自己听说的，去不去由 TA 自己决定。",
};

export interface Refusal {
  /** 给玩家看的一句。 */
  text: string;
  /** 认得的原因码（页面据此决定：活动没开放就收起按钮、TA 已经去了就重读状态）；认不出是 null。 */
  reason: keyof typeof REFUSALS | null;
}

export function refusalOf(error: unknown): Refusal {
  const e = toApiError(error);
  const reason = typeof e.details?.reason === "string" ? e.details.reason : null;
  if (reason && Object.prototype.hasOwnProperty.call(REFUSALS, reason)) return { text: REFUSALS[reason], reason: reason as Refusal["reason"] };
  // 认不出的出错：只给玩家看的那一句；错误码、原因码、request_id 不上页面（用户 10-09 定稿 X-09／U-26：玩家端不显示技术代码、请求编号、错误码，也不做“展开看详情”的折叠区，要查只在运营后台查）。
  return { text: e.playerMessage, reason: null };
}

/** 活动列表的查询键（公开、不分账号）。 */
export const planetEventsKey = ["planet-events"] as const;
/** 这只宠物告诉过的活动（分账号、分宠物：切宠物就是另一条，按钮状态不串）。 */
export const eventTellsKey = (userId: string | null, petId: string | null) => ["planet-event-tells", userId ?? "-", petId ?? "-"] as const;
